import { message, Tooltip } from "antd";
import {
  SettingOutlined,
  StarOutlined,
  StarFilled,
  BranchesOutlined,
} from "@ant-design/icons";
import { useTenantNavigation } from "../../../hooks/useTenantNavigation.js";
import axios from "../../../axios-interceptor.js";
import { fetchUserInfo } from "../../../redux/actions/userAction";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { showLoader } from "../../../utils/showloader";
import { useState, useRef, useEffect } from "react";
import { spaceColorBgMapping } from "../../../utils/colorBgColorMap";
import SvgIcons from "../../layout/assets/SvgIcons";
import { getUserSpaceEntitlements } from "../../../utils/currentUserDetail.js";


// True while an element's text is cut off by its CSS line clamp - the card shows
// a tooltip only then, so a label that already reads in full is not repeated on
// hover. Re-measured on resize: the cards reflow with the grid, and a wider card
// can fit text that did not.
const useIsTruncated = (ref, text) => {
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const measure = () => setIsTruncated(element.scrollHeight > element.clientHeight);
    measure();

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [ref, text]);

  return isTruncated;
};

const BACKEND_API = process.env.REACT_APP_BACKEND_API;
let tenantId = localStorage.getItem("tenantId")

const SpaceNewCard = (props) => {
  const { spaceId, name, description, userInfo, spaceColor = "", spaceIcon = "", userGroupSpaces = [], translations = {} } = props;
  const { t, i18n } = useTranslation();
  // Show the space name/description in the viewer's language, falling back to the
  // canonical values when this language has no translation.
  const localizedSpace = translations?.[i18n.language] || {};
  const displayName = localizedSpace.name || name;
  const displayDescription = localizedSpace.description || description;
  const dispatch = useDispatch();
  const navigate = useTenantNavigation();
  const favoriteSpaces = userInfo.favoriteSpaces || [];
  const [isHovering, setIsHovering] = useState(false);
  const nameRef = useRef(null);
  const descriptionRef = useRef(null);
  const isNameTruncated = useIsTruncated(nameRef, displayName);
  const isDescriptionTruncated = useIsTruncated(descriptionRef, displayDescription);
  let spaceEntitlements = getUserSpaceEntitlements(spaceId, userInfo?.userAccess || [], userGroupSpaces)


  const navigateToSettings = (event, page) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`/app/space/${spaceId}/${page}`)
    // navigate(`/app/space/${spaceId}/settings?tab=general`);
  };
  const navigateToDocuments = (event) => {
    event.preventDefault();
    event.stopPropagation();
    navigate(`/app/space/${spaceId}/documents`)
  }

  const updateFavorite = async (e, addFav) => {
    e.preventDefault();
    e.stopPropagation();

    let existingFavourites = favoriteSpaces || []
    if (!addFav) {
      existingFavourites = existingFavourites.filter(fav => fav !== spaceId)
    } else {
      existingFavourites = [...existingFavourites, spaceId]
    }
    let spaceObj = { favouriteSpaces: existingFavourites }
    showLoader(true)
    try {
      let resp = await axios({
        method: "PUT",
        url: `${BACKEND_API}/user`,
        data: {
          ...spaceObj,
          userId: userInfo.userId
          // tenantId: tenantId
        },
        headers: {
          userId: userInfo.userId,
          tenantId: tenantId
        },
      })
      if (resp?.status === 200) {
        let displayMsg = addFav ? t("Space added to favourites") : t("Space removed from favourites")
        message.success(displayMsg);
        dispatch(fetchUserInfo())
        // showLoader(false)
      } else {
        message.error(t("Failed to add favorite. Please try again."))
      }

    } catch (error) {
    } showLoader(false)
  }

  const handleMouseOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsHovering(true);
  };

  const handleMouseOut = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsHovering(false);
  };
  // Get dynamic colors for theming
  const bgColor = spaceColorBgMapping[spaceColor] || "#f5eeff";
  const iconColor = spaceColor || "#722ed1";
  return (<div
    className="group relative w-full h-48 bg-white rounded-xl cursor-pointer border border-gray-200/50 hover:border-gray-300 hover:shadow-lg transition-all duration-300 ease-out transform hover:-translate-y-0.5 overflow-hidden"
    style={{ backgroundColor: bgColor }}
    onClick={navigateToDocuments}
    onMouseEnter={handleMouseOver}
    onMouseLeave={handleMouseOut}
  >      {/* Main Content */}
    <div className="p-4 h-full flex flex-col relative z-10">        {/* Header with Icon and Favorite */}
      <div className="flex justify-between items-start mb-3">
        {/* Space Icon */}
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all duration-300 ease-out shadow-sm ${isHovering ? 'scale-105 shadow-md' : ''
            }`}
          style={{
            backgroundColor: isHovering ? iconColor : "#ffffff",
            border: `1.5px solid ${isHovering ? iconColor : 'rgba(0,0,0,0.06)'}`
          }}
        >
          <span
            className="flex text-lg transition-all duration-300 ease-out"
            style={{
              color: isHovering ? "#ffffff" : iconColor,
              transform: isHovering ? 'scale(1.05)' : 'scale(1)'
            }}
          >
            <SvgIcons type={spaceIcon} />
          </span>
        </div>

        {/* Favorite Star */}
        <div className="opacity-60 group-hover:opacity-100 transition-all duration-300">
          {favoriteSpaces.includes(spaceId) ? (
            <StarFilled
              // style={{color: iconColor}}
              className="w-5 h-5 text-yellow-500 cursor-pointer hover:text-yellow-600 hover:scale-110 transition-all duration-200"
              onClick={(e) => updateFavorite(e, false)}
            />
          ) : (
            <StarOutlined
              className="w-5 h-5 text-gray-300 cursor-pointer hover:text-yellow-500 hover:scale-110 transition-all duration-200"
              onClick={(e) => updateFavorite(e, true)}
            />
          )}
        </div>
      </div>          { /* Content Area */}
      <div className="flex-1 flex flex-col min-h-0">
        <Tooltip title={isNameTruncated ? displayName : ""} placement="topLeft">
          <h3
            ref={nameRef}
            className="text-sm font-bold text-gray-900 mb-1 leading-5 line-clamp-1"
          >
            {displayName}
          </h3>
        </Tooltip>

        {/* One clamped line, with the rest on hover. The card is a fixed height and
            the stats row below it is not negotiable, so a second line has nowhere
            to go - it used to render straight through the divider. The full text
            stays in the DOM (no word slicing) so the clamp can place the ellipsis
            where the line actually ends, and the tooltip can show all of it. */}
        <Tooltip title={isDescriptionTruncated ? displayDescription : ""} placement="topLeft">
          <p
            ref={descriptionRef}
            className="text-xs text-gray-600 leading-4 line-clamp-1"
          >
            {displayDescription}
          </p>
        </Tooltip>

      </div>          {/* Stats Section */}
      <div className="mt-3 pt-3 border-t border-white/50">
        <div className="flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <div className="text-center">
              <div className="text-[9px] font-medium text-gray-500 mb-0.5">{t("Requests")}</div>
              <div className="text-xs font-bold text-gray-800">{props.requestsCount || 0}</div>
            </div>
            <div className="text-center">
              <div className="text-[9px] font-medium text-gray-500 mb-0.5">{t("Members")}</div>
              <div className="text-xs font-bold text-gray-800">{props.membersCount || 0}</div>
            </div>
          </div>

          {/* Settings Button */}
          {
            userInfo?.isAdmin || spaceEntitlements.includes('Space_Update') ?
              <div className="flex gap-1">
                <Tooltip title={t("Space Settings")}> 
                <button
                  onClick={(e) => navigateToSettings(e, 'space-settings')}
                  className={`p-1.5 h-8 w-8 text-gray-400 hover:text-gray-700 rounded-lg transition-all duration-300 hover:scale-105 ${isHovering ? 'bg-white/80 hover:bg-white shadow-sm' : 'hover:bg-white/60'
                    }`}
                >
                  <SettingOutlined className="w-3.5 h-3.5" />
                </button>
                </Tooltip>
                <Tooltip title={t("Workflow")}>
                  <button
                    onClick={(e) => navigateToSettings(e, 'workflow')}
                    className={`p-1.5 h-8 w-8 text-gray-400 hover:text-gray-700 rounded-lg transition-all duration-300 hover:scale-105 ${isHovering ? 'bg-white/80 hover:bg-white shadow-sm' : 'hover:bg-white/60'
                      }`}
                  >
                    <BranchesOutlined className="w-3.5 h-3.5" />
                  </button>
                </Tooltip>
              </div>
              : null
          }
        </div>
      </div>
    </div>      {/* Subtle gradient overlay on hover */}
    <div className={`absolute inset-0 bg-gradient-to-br from-white/10 to-transparent pointer-events-none transition-opacity duration-300 ${isHovering ? 'opacity-100' : 'opacity-0'
      }`} />
  </div>
  );
};

export default SpaceNewCard;
